import React, {useState, useRef} from 'react';
import html2canvas from 'html2canvas';
import {Modal, ModalContent, ModalHeader, ModalBody} from '@/components/modal';
import {KeenIcon} from '@/components';
import axios from 'axios';
import {CommonHexagonBadge} from '@/partials/common';
import {LoaderType, Loading} from '@/layouts/buddy/loading';
import {enqueueSnackbar} from 'notistack';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const API_FEEDBACK = `${import.meta.env.VITE_APP_API_URL}/feedback`;

const FeedbackModal: React.FC<FeedbackModalProps> = ({isOpen, onClose}) => {
    const [feedback, setFeedback] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const modalRef = useRef<HTMLDivElement | null>(null);

    const submitFeedback = async () => {
        setSubmitting(true);
        try {
            // Close the modal completely
            onClose();

            // Wait for the modal and overlay to be removed from the DOM
            await new Promise((resolve) => setTimeout(resolve, 150));

            // Take screenshot
            const canvas = await html2canvas(document.body);
            const dataUrl = canvas.toDataURL('image/png');
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], 'screenshot.png', {type: 'image/png'});

            const metadata = {
                url: window.location.href,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            };

            const formData = new FormData();
            formData.append('file', file);
            formData.append('feedback', feedback);
            formData.append('metadata', JSON.stringify(metadata));

            await axios.post(`${API_FEEDBACK}/submit`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                },
            });

            enqueueSnackbar('Thanks for your feedback! 🎉', {variant: 'success'});
            setFeedback(''); // clear textarea
        } catch (error) {
            console.error('Error submitting feedback:', error);
            enqueueSnackbar('Failed to submit feedback.', {variant: 'error'});

            // Optional: reopen modal if you want users to retry
            // setIsOpen(true); or a parent-level signal to show modal again
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} className="!flex" ref={modalRef}>
            <ModalContent className="container-fixed px-30 overflow-hidden pt-7.5 my-[3%] w-full">
                <ModalHeader className="p-0 border-0">
                    <div className="flex items-center justify-between flex-wrap grow gap-5 pb-7.5">
                        <div className="flex items-baseline space-x-2">
                            <KeenIcon icon="note-2" className="text-3xl"/>
                            <h1 className="text-xl font-semibold leading-none text-gray-900">
                                We’d love your feedback
                            </h1>
                        </div>
                        <button className="btn btn-sm btn-light" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </ModalHeader>

                <ModalBody className="scrollable-y py-0 mb-5 ps-0 pe-3 -me-5.5">
                    <div className="flex grow gap-5 lg:gap-7.5">
                        <div className="flex flex-col items-stretch grow gap-5 lg:gap-7.5">
                            <div className="card rounded-xl">
                                <div
                                    className="flex items-center justify-between grow gap-2 p-5 bg-no-repeat bg-[length:660px_310px] upgrade-bg"
                                    style={{backgroundPosition: '121% 41%'}}
                                >
                                    <div className="flex items-center gap-4">
                                        <CommonHexagonBadge
                                            stroke="stroke-success-clarity"
                                            fill="fill-success-light"
                                            size="size-[50px]"
                                            badge={<KeenIcon icon="information-4" className="text-xl text-success"/>}
                                        />
                                        <div className="flex flex-col gap-1.5">
                                            <div className="flex items-center gap-2.5">
                                                <span className="text-base font-medium text-gray-900">
                                                    Help shape Buddy
                                                </span>
                                            </div>
                                            <div className="text-2sm text-gray-800">
                                                Buddy is still in an early stage. Your feedback helps us make it
                                                smarter,
                                                faster, and more useful for you and your team.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card pb-2.5">
                                <div className="card-header" id="upload_document">
                                    <h3 className="card-title">What works well? What could be better?</h3>
                                </div>
                                <div className="card-body grid gap-5">
                                    {!submitting ? (
                                        <>
                                            <textarea
                                                rows={5}
                                                className="resize-none border border-gray-400 p-3 rounded-md text-sm w-full h-40 focus:outline-none"
                                                value={feedback}
                                                onChange={(e) => setFeedback(e.target.value)}
                                                placeholder="Your honest thoughts help us improve..."
                                            />
                                            <div className="flex justify-end pt-2.5">
                                                <button
                                                    onClick={submitFeedback}
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    disabled={submitting || feedback.trim() === ''}
                                                >
                                                    {submitting ? 'Sending...' : 'Submit Feedback'}
                                                    <KeenIcon icon="arrow-right"/>
                                                </button>

                                            </div>
                                        </>
                                    ) : (
                                        <Loading
                                            loadingText="Submitting Feedback"
                                            loaderType={LoaderType.ROBOT_WAITING}
                                            loading={submitting}
                                            delay={0}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default FeedbackModal;
